import { useState } from "react";
import { T } from "../utils/theme";
import { api, ENDPOINTS } from "../utils/api";
import { Field, LoadingBtn, AlertBox } from "../components/UI";
import { Shield, Eye, EyeOff, ArrowRight, ChevronDown } from "lucide-react";

const ROLES = [
  { value: "superadmin", label: "⚡ Super Admin" },
  { value: "admin",      label: "🛡 Admin"       },
  { value: "company",    label: "🏢 Entreprise"  },
  { value: "hacker",     label: "🔓 Hacker"      },
];

const LoginPage = ({ onLogin }) => {
  const [form, setForm]     = useState({ email: "", password: "", role: "superadmin" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await api(ENDPOINTS.login, {
        method: "POST",
        body: JSON.stringify({
          email:    form.email,
          password: form.password,
          role:     form.role,
        }),
      });

      const token =
        data.access_token        ||
        data.accessToken         ||
        data.token               ||
        data?.data?.access_token;

      const user =
        data?.data               ||
        data.user                ||
        data.admin               ||
        { email: form.email, role: form.role };

      if (!token) {
        setError("Token introuvable dans la réponse. Vérifiez vos identifiants.");
        return;
      }

      onLogin(token, user);
    } catch (err) {
      setError(err.message || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: T.bg,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Background grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `linear-gradient(${T.border}88 1px, transparent 1px), linear-gradient(90deg, ${T.border}88 1px, transparent 1px)`,
        backgroundSize: "48px 48px",
        opacity: .35,
      }} />

      {/* Radial glow top left */}
      <div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        background: `radial-gradient(circle, ${T.blue}18, transparent 70%)`,
        top: -200, left: -200, pointerEvents: "none",
      }} />

      {/* Radial glow bottom right */}
      <div style={{
        position: "absolute", width: 500, height: 500, borderRadius: "50%",
        background: `radial-gradient(circle, ${T.purple}15, transparent 70%)`,
        bottom: -100, right: -100, pointerEvents: "none",
      }} />

      <div className="fade-up" style={{ width: 420, position: "relative", zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 64, height: 64, borderRadius: 18,
            background: `linear-gradient(135deg, ${T.blue}, ${T.purple})`,
            marginBottom: 16,
            boxShadow: `0 0 40px ${T.blueGlow}, 0 0 80px ${T.blueGlow}`,
            animation: "glow 3s ease-in-out infinite",
          }}>
            <Shield size={28} color="#fff" />
          </div>
          <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 28, color: T.text, letterSpacing: ".03em" }}>200BOUNTY</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 5, letterSpacing: ".06em" }}>PANNEAU D'ADMINISTRATION</div>
        </div>

        {/* Form card */}
        <div style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          padding: "32px 30px",
          boxShadow: `0 24px 64px rgba(0,0,0,.5), 0 0 0 1px ${T.border}`,
        }}>
          <h2 style={{ fontFamily: T.display, fontWeight: 700, fontSize: 17, color: T.text, marginBottom: 6 }}>Connexion</h2>
          <p style={{ fontSize: 12, color: T.muted, marginBottom: 24 }}>Accès réservé aux administrateurs</p>

          {error && (
            <div style={{ marginBottom: 16 }}>
              <AlertBox type="error">{error}</AlertBox>
            </div>
          )}

          <form onSubmit={handleSubmit}>

            {/* Rôle */}
            <Field label="Rôle">
              <div style={{ position: "relative" }}>
                <select
                  value={form.role}
                  onChange={e => set("role", e.target.value)}
                  required
                  style={{
                    width: "100%",
                    appearance: "none",
                    WebkitAppearance: "none",
                    background: T.surfaceHigh || T.surface,
                    color: T.text,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    padding: "10px 36px 10px 12px",
                    fontSize: 13,
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  color={T.muted}
                  style={{
                    position: "absolute", right: 12, top: "50%",
                    transform: "translateY(-50%)", pointerEvents: "none",
                  }}
                />
              </div>
            </Field>

            {/* Email */}
            <Field label="Adresse email">
              <input
                type="email"
                placeholder="admin@200bounty.com"
                value={form.email}
                onChange={e => set("email", e.target.value)}
                required
                autoFocus
              />
            </Field>

            {/* Mot de passe */}
            <Field label="Mot de passe">
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  placeholder="••••••••••••"
                  value={form.password}
                  onChange={e => set("password", e.target.value)}
                  required
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  style={{
                    position: "absolute", right: 13, top: "50%",
                    transform: "translateY(-50%)", background: "none",
                    border: "none", color: T.muted, cursor: "pointer", padding: 0,
                  }}
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>

            <div style={{ marginTop: 24 }}>
              <LoadingBtn
                loading={loading}
                className="btn btn-primary btn-lg"
                style={{ width: "100%", justifyContent: "center" }}
              >
                Se connecter <ArrowRight size={15} />
              </LoadingBtn>
            </div>
          </form>
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: T.muted, marginTop: 20 }}>
          200Bounty Platform v2.0 · Accès sécurisé
        </p>
      </div>
    </div>
  );
};

export default LoginPage;